import { z } from 'zod';
import { employmentSchema, type EmploymentDto, type Scope } from '@eombor/contracts';
import { apiClient } from '../../auth/model/auth.store.js';
import type { ApiClient } from '../../../shared/api/client.js';

/**
 * `GET /users` javobining ish vaqtidagi sxemasi.
 * Manba: `identity/application/user.service.ts` dagi `UserListItem`.
 *
 * `orgUnit*`, `position*` va `subdivision*` — xodimning BUGUNGI aktiv ish
 * joyi. Ular `null` bo'lishi mumkin: xodim tizimda bor, lekin hech qayerga
 * biriktirilmagan.
 *
 * `employmentId` — o'sha aktiv yozuvning identifikatori. Ish joyini
 * tahrirlash oynasi aynan shunga qarab yo'l tanlaydi: bor bo'lsa
 * `PATCH /employments/:id`, `null` bo'lsa `POST /users/:id/employments`.
 */
export const staffSchema = z.object({
  id: z.string(),
  fullName: z.string(),
  username: z.string(),
  jshshir: z.string(),
  isSuperadmin: z.boolean(),
  telegramLinked: z.boolean(),
  employmentId: z.string().nullable(),
  orgUnitId: z.string().nullable(),
  orgUnitName: z.string().nullable(),
  positionId: z.string().nullable(),
  positionName: z.string().nullable(),
  subdivisionId: z.string().nullable(),
  subdivisionName: z.string().nullable(),
});

export type StaffDto = z.infer<typeof staffSchema>;

export const staffPageSchema = z.object({
  items: z.array(staffSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});

export type StaffPage = z.infer<typeof staffPageSchema>;

export interface StaffQuery {
  q?: string;
  page?: number;
  limit?: number;
}

/**
 * Parol tiklash natijasi.
 *
 * `password` HAR DOIM keladi: administrator uni ekrandan o'qib, xodimga
 * o'zi beradi. `delivery` esa Telegramga NUSXA ketgan-ketmaganini aytadi.
 */
export const passwordResetSchema = z.object({
  delivery: z.enum(['telegram', 'onscreen']),
  password: z.string(),
  fullName: z.string(),
  username: z.string(),
});

export type PasswordResetDto = z.infer<typeof passwordResetSchema>;

/**
 * JSHSHIR bo'yicha KADRLAR BAZASIDAN topilgan xodim.
 *
 * `userId` — unga bog'langan tizim hisobi. `null` bo'lsa xodim hali
 * qo'shilmagan; `userActive` esa hisob allaqachon faol ekanini bildiradi
 * (bunday xodimni qayta qo'shib bo'lmaydi).
 */
export const personnelLookupSchema = z.object({
  jshshir: z.string(),
  fullName: z.string(),
  phone: z.string().nullable(),
  orgUnitId: z.string().nullable(),
  orgUnitName: z.string().nullable(),
  positionId: z.string().nullable(),
  positionName: z.string().nullable(),
  userId: z.string().nullable(),
  username: z.string().nullable(),
  userActive: z.boolean(),
});

export type PersonnelLookupDto = z.infer<typeof personnelLookupSchema>;

/** Yaratish natijasi — parol tiklash bilan bir xil shakl, ustiga hisob id si. */
export const createStaffResultSchema = z.object({
  userId: z.string(),
  fullName: z.string(),
  username: z.string(),
  delivery: z.enum(['telegram', 'onscreen']),
  password: z.string(),
  reactivated: z.boolean(),
});

export type CreateStaffResultDto = z.infer<typeof createStaffResultSchema>;

export interface CreateStaffBody {
  jshshir: string;
  roleId: string;
  scope: Scope;
  /**
   * Ish joyi — FAQAT kadrlar bazasida u bo'lmaganda yuboriladi.
   *
   * Bor bo'lsa server rad etadi: kadrlar bazasi yagona manba bo'lib
   * qoladi va bir xil xodim ikki xil korxonada ko'rinmaydi.
   */
  orgUnitId?: string;
  positionId?: string;
  /** Lavozim QO'LDA yozilganda — `positionId` o'rniga nomi. */
  positionName?: string;
}

/** Lavozim — ish joyi ko'rsatilganda ro'yxatdan tanlanadi yoki qo'lda yoziladi. */
export const staffPositionSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
});

export type StaffPositionDto = z.infer<typeof staffPositionSchema>;

const staffPositionPageSchema = z.object({ items: z.array(staffPositionSchema) });

/**
 * Ish joyini biriktirish / o'zgartirish so'rovi.
 *
 * `subdivisionId: null` — bo'linmani BEKOR QILISH. Shuning uchun u
 * `undefined` dan farq qiladi va tanlanmagan holat so'rovga umuman
 * qo'shilmaydi: server `undefined` ni «tegilmasin» deb o'qiydi.
 */
export interface WorkplaceBody {
  orgUnitId?: string;
  /** `null` — lavozimni BEKOR QILISH. Lavozim majburiy emas. */
  positionId?: string | null;
  /** Lavozim QO'LDA yozilganda — nomi; server uni ma'lumotnomaga qo'shadi. */
  positionName?: string;
  subdivisionId?: string | null;
}

export interface StaffApi {
  list: (query?: StaffQuery) => Promise<StaffPage>;
  lookup: (jshshir: string) => Promise<PersonnelLookupDto>;
  create: (body: CreateStaffBody) => Promise<CreateStaffResultDto>;
  resetPassword: (userId: string) => Promise<PasswordResetDto>;
  /** Lavozimlar ro'yxati — tanlash uchun; yangisi qo'lda ham yoziladi. */
  positions: () => Promise<StaffPositionDto[]>;
  /** Ish joyi YO'Q xodimga yangisini yaratadi. */
  createEmployment: (userId: string, body: WorkplaceBody) => Promise<EmploymentDto>;
  /** Mavjud ish joyini o'zgartiradi — bo'linma, lavozim. */
  updateEmployment: (employmentId: string, body: WorkplaceBody) => Promise<EmploymentDto>;
}

/** Bo'sh `q` so'rovga QO'SHILMAYDI — backendda u baribir e'tiborsiz qoladi. */
const queryString = (query: StaffQuery): string => {
  const params = new URLSearchParams({
    page: String(query.page ?? 1),
    limit: String(query.limit ?? 20),
  });
  if (query.q !== undefined && query.q !== '') params.set('q', query.q);
  return params.toString();
};

export const createStaffApi = (client: ApiClient): StaffApi => ({
  list: (query = {}) =>
    client.apiFetch(`/users?${queryString(query)}`, { schema: staffPageSchema }),

  lookup: (jshshir) =>
    client.apiFetch(`/users/lookup?jshshir=${encodeURIComponent(jshshir)}`, {
      schema: personnelLookupSchema,
    }),

  create: (body) =>
    client.apiFetch('/users', {
      method: 'POST',
      body: JSON.stringify(body),
      schema: createStaffResultSchema,
    }),

  createEmployment: (userId, body) =>
    client.apiFetch(`/users/${encodeURIComponent(userId)}/employments`, {
      method: 'POST',
      body: JSON.stringify(body),
      schema: employmentSchema,
    }),

  updateEmployment: (employmentId, body) =>
    client.apiFetch(`/employments/${encodeURIComponent(employmentId)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
      schema: employmentSchema,
    }),

  positions: async () =>
    (await client.apiFetch('/positions?page=1&limit=200', { schema: staffPositionPageSchema }))
      .items,

  resetPassword: (userId) =>
    client.apiFetch(`/users/${encodeURIComponent(userId)}/password-reset`, {
      method: 'POST',
      schema: passwordResetSchema,
    }),
});

export const staffApi = createStaffApi(apiClient);
